import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase } from './supabase';
import { 
  FiAlertTriangle, 
  FiTrash2, 
  FiXCircle, 
  FiArrowLeft 
} from 'react-icons/fi';
import { useI18n } from './i18n';
import './auth-pages.css';

function DeleteAccountConfirmPage() {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { resetReauthenticationStatus, setNextPageAfterReauth } = useAuth();
  const { t } = useI18n();

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      // Use Supabase Edge Function to delete the user account
      const { data, error: deleteError } = await supabase.functions.invoke('delete-account', {
        method: 'POST',
      });
      
      if (deleteError) throw deleteError;

      if (data?.warnings?.length) {
        console.warn('Delete account warnings:', data.warnings);
      }

      if (!data?.deleted) {
        throw new Error(t('auth.deleteFailed'));
      }
      
      // The Edge Function deleted the auth user. Local cleanup should not block
      // showing completion, since the server session may already be invalid.
      const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' });
      if (signOutError) {
        console.warn('Post-delete sign out warning:', signOutError.message || signOutError);
      }
      resetReauthenticationStatus();
      navigate('/login', { replace: true, state: { accountDeleted: true } });
    } catch (error) {
      setIsDeleting(false);
      setError(error?.message || String(error));
      
      // If error indicates need for reauthentication
      if (error?.message && error.message.includes('recent authentication')) {
        setNextPageAfterReauth('/dashboard/delete-account-confirm');
        navigate('/dashboard/reauthenticate');
      }
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t('auth.deleteTitle')}</h1>
          <p>{t('auth.deleteSubtitle')}</p>
        </div>

        <div className="auth-warning" style={{ marginBottom: '2rem' }}>
          <FiAlertTriangle size={24} />
          <div>
            <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {t('auth.deleteWarningTitle')}
            </p>
            <p>
              {t('auth.deleteWarningBody')}
            </p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>{t('auth.deleteHistory')}</li>
              <li>{t('auth.deleteSettings')}</li>
              <li>{t('auth.deletePremium')}</li>
            </ul>
          </div>
        </div>

        {error && (
          <div className="auth-error">
            <FiXCircle />
            <span>{error}</span>
          </div>
        )}

        <button 
          className="auth-btn danger-btn" 
          onClick={confirmDelete}
          disabled={isDeleting}
        >
          <FiTrash2 /> {isDeleting ? t('auth.deleting') : t('auth.confirmDelete')}
        </button>

        <button 
          className="auth-btn secondary-btn" 
          onClick={() => navigate('/dashboard/profile')}
          disabled={isDeleting}
        >
          <FiArrowLeft /> {t('auth.cancelDelete')}
        </button>
      </div>
    </div>
  );
}

export default DeleteAccountConfirmPage;
